import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';

import {ProcessTaskStatus} from 'processes/__generated/ProcessTaskStatus';
import {ProcessTaskStatusLifecycle} from 'processes/__generated/ProcessTaskStatusLifecycle';
import ProcessTaskStatusDisplay from 'processes/tables/ProcessTaskStatusDisplay';
import getTestProps from 'test/lib/cypress/getTestProps';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import {GRAY_DARKER, GRAY_LIGHTER} from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';

import {findValidOutStatuses, isTerminalStatus, sortTaskStatuses} from './TaskStatusUtil';

interface TaskStatusEditorProps {
  status: ProcessTaskStatus;
  statusLifecycle: ProcessTaskStatusLifecycle;
  lastUpdated: string;
  onSelect: (status: ProcessTaskStatus) => void;
  disabled?: boolean;
}

// TODO(BNCH-47168): Refactor with ProcessTaskStatusSelector.
export default function TaskStatusEditor(props: TaskStatusEditorProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const isDisabled = props.disabled || isTerminalStatus(props.status.id, props.statusLifecycle);

  return (
    <>
      <Dropdown
        menu={
          <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.FULL}>
            {sortTaskStatuses(findValidOutStatuses(props.status, props.statusLifecycle)).map((status) => (
              <Option
                contentClassName={css(styles.option)}
                key={status.displayName}
                value={status}
                onSelect={props.onSelect}
              >
                <ProcessTaskStatusDisplay
                  statusType={status.statusType}
                  displayName={status.displayName}
                  showTerminalStatusTooltip={false}
                />
              </Option>
            ))}
          </Menu>
        }
        isOpen={isOpen}
        onToggle={() => {
          if (!isDisabled) {
            setIsOpen(!isOpen);
          }
        }}
      >
        <div
          className={classNames(
            'editableField',
            css(!isDisabled && styles.dropdownButton, isOpen && styles.dropdownButtonOpen)
          )}
          {...getTestProps({component: 'TaskStatusEditor', element: 'dropdownButton'})}
        >
          <span className={classNames('u-lr-split', css(styles.buttonWrapper))}>
            <span className={css(styles.statusWrapper)}>
              <ProcessTaskStatusDisplay
                statusType={props.status.statusType}
                displayName={props.status.displayName}
                showTerminalStatusTooltip={false}
              />
              <Tooltip title={`Status last updated on ${props.lastUpdated}`} placement={TooltipPlacement.TOP}>
                <BenchlingIcon
                  className={classNames('infoIcon', css(styles.infoIcon, isOpen && styles.infoIconOpen))}
                  icon="info-circle"
                  {...getTestProps({component: 'TaskStatusEditor', element: 'infoIcon'})}
                />
              </Tooltip>
            </span>
            {!isDisabled && (
              <BenchlingIcon
                className={classNames(
                  'dropdownIcon',
                  css(styles.dropdownIcon, isOpen && styles.dropdownIconOpen)
                )}
                icon="chevron-down"
                {...getTestProps({component: 'TaskStatusEditor', element: 'dropdownIcon'})}
              />
            )}
          </span>
        </div>
      </Dropdown>
    </>
  );
}

export const styles = StyleSheet.create({
  dropdownButton: {
    cursor: 'pointer',
    ':hover .dropdownIcon': {
      visibility: 'visible',
    },
  },
  dropdownButtonOpen: {
    backgroundColor: GRAY_LIGHTER,
  },
  dropdownIcon: {
    visibility: 'hidden',
    marginRight: '8px',
  },
  dropdownIconOpen: {
    visibility: 'visible',
  },
  infoIcon: {
    color: GRAY_DARKER,
    marginLeft: '4px',
    visibility: 'visible',
  },
  infoIconOpen: {
    visibility: 'hidden',
  },
  option: {
    paddingLeft: '8px',
  },
  buttonWrapper: {
    width: '100%',
  },
  statusWrapper: {
    display: 'inline-flex',
    alignItems: 'center',
  },
});
